import { gql } from '@apollo/client';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { CheckoutStep, PAYMENT_STEP, SHIPPING_STEP, isValidStep } from '../../lib/checkout_step';
import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import { isCheckoutLocalPickupOnly } from '../../lib/checkoutShipping';
import { IUrl, useURL } from '@reverbdotcom/commons/src/url_context';
import { CheckoutDisplayStepHookFields } from '@reverbdotcom/commons/src/gql/graphql';
import { InjectedRouter } from 'react-router';
import { omit } from 'lodash';
import { CHECKOUT_ERROR_KEY } from '../../lib/checkoutPaths';

type Checkout = CheckoutDisplayStepHookFields.Fragment;

export function useCheckoutDisplayStep({ checkout, router }: { checkout: Checkout, router: InjectedRouter }) {
  const user = useUser();
  const url = useURL();
  const step = findCheckoutStep(checkout, user, url);

  function setStep(checkoutStep: CheckoutStep) {
    const nextQuery = {
      ...omit(url.query, CHECKOUT_ERROR_KEY),
      step: checkoutStep,
    };

    router.push({
      query: nextQuery,
      pathname: url.pathname,
    });
  }

  return {
    setStep,
    step,
  };
}

function findCheckoutStep(checkout: Checkout, user: Partial<IUser>, url: IUrl): CheckoutStep {
  if (isValidStep(url.query.step)) {
    return url.query.step;
  }

  if (isPaymentStep(checkout, user)) {
    return PAYMENT_STEP;
  }

  return SHIPPING_STEP;
}

function isPaymentStep(checkout: Checkout, user: Partial<IUser>) {
  if (!checkout) {
    return false;
  }

  const isLocalPickupOnly = isCheckoutLocalPickupOnly(checkout);
  const isVisibleForLocalPickupOnlyCheckout = isLocalPickupOnly && (!user.loggedOut || checkout.guest.isComplete);
  const isVisibleForShippedCheckout = checkout.shippingAddress?.isComplete;

  return isVisibleForLocalPickupOnlyCheckout || isVisibleForShippedCheckout;
}

export const CheckoutDisplayStepHookFragment = gql`
  fragment CheckoutDisplayStepHookFields on Checkout {
    _id
    paymentMethod {
      type
      isComplete
    }
    orders {
      shippingMethod {
        type
      }
    }
    guest {
      isComplete
    }
    shippingAddress {
      _id
      isComplete
    }
  }
`;
